import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import moment from 'moment';
import './CollectionAnalysis.scss';

import SearchInput from '../SearchInput/SearchInput';
import CollectionAnalysisResult from './CollectionAnalysisResult';
import CollectionSearch from './CollectionSearch';
import {
  fetchCollectionAnalysisList,
  fetchCollectionAnalysisSummary,
  updateCollectionAnalysisAdjustment
} from '../../api/collection_analysis';

const PASSING_SCORE = 5;
const DEBOUNCE_MS = 300;

const MIN_SCORE_OPTIONS = [
  { label: 'Any score', value: '' },
  { label: `≥ ${PASSING_SCORE} (passing)`, value: `${PASSING_SCORE}` },
  { label: '≥ 6', value: '6' },
  { label: '≥ 7', value: '7' },
  { label: '≥ 8', value: '8' },
  { label: '≥ 9', value: '9' }
];

const SORT_OPTIONS = [
  { label: 'Score', value: 'score' },
  { label: 'Cover Image Score', value: 'coverImageScore' },
  { label: 'Recently Scored', value: 'scoredAt' },
  { label: 'Product Count', value: 'productCount' },
  { label: 'In Stock %', value: 'inStockPct' },
  { label: 'Freshness', value: 'freshness' }
];

const CollectionAnalysis = () => {
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [searchValue, setSearchValue] = useState('');
  const [query, setQuery] = useState('');
  const [minScore, setMinScore] = useState('');
  const [sortBy, setSortBy] = useState('score');

  // Data fetching
  useEffect(() => {
    fetchCollectionAnalysisSummary({ passingScore: PASSING_SCORE })
      .then(setSummary)
      .catch(() => window.ALERT.error('Failed to load the score summary.'));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchCollectionAnalysisList({ page, query, minScore, sortBy })
      .then(data => {
        setResults(data.results || []);
        setTotal(data.total || 0);
        setPageSize(data.pageSize || 50);
      })
      .catch(() => window.ALERT.error('Failed to load scored collections.'))
      .finally(() => setIsLoading(false));
  }, [page, query, minScore, sortBy]);

  const debouncedSetQuery = useCallback(
    _.debounce(value => {
      setQuery(value);
      setPage(0);
    }, DEBOUNCE_MS),
    []
  );
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const onSearchChange = ({ target }) => {
    setSearchValue(target.value);
    debouncedSetQuery(target.value);
  };

  const changeMinScore = value => {
    setMinScore(value);
    setPage(0);
  };

  const changeSortBy = value => {
    setSortBy(value);
    setPage(0);
  };

  const saveAdjustment = async (analysisId, updates) => {
    const { adjustment } = await updateCollectionAnalysisAdjustment(analysisId, updates);
    setResults(currentResults => currentResults.map(result => (result.id === analysisId ? { ...result, ...adjustment } : result)));
    // Adjustments shift the distribution + passing counts, so refresh the rollup. Ignore
    // failures — the save already succeeded and the stale summary self-corrects on reload.
    fetchCollectionAnalysisSummary({ passingScore: PASSING_SCORE })
      .then(setSummary)
      .catch(() => {});
  };

  // Summary stats — render placeholder cells while the summary loads so the header doesn't pop in.
  const passingPct = summary?.totalScored ? Math.round((summary.passingCount / summary.totalScored) * 100) : 0;
  const summaryStats = [
    { label: 'Scored', value: summary ? summary.totalScored.toLocaleString() : '—' },
    { label: `Passing (≥ ${PASSING_SCORE})`, value: summary ? `${summary.passingCount.toLocaleString()} · ${passingPct}%` : '—' },
    {
      label: 'Collections in search',
      value: summary?.collectionsInIndex != null ? summary.collectionsInIndex.toLocaleString() : '—'
    },
    {
      label: 'Products (hydratable)',
      value: summary?.productsInIndex != null ? summary.productsInIndex.toLocaleString() : '—'
    },
    { label: 'Avg Score', value: summary?.avgScore != null ? summary.avgScore.toFixed(1) : '—' },
    { label: 'Last Scored', value: summary?.lastScoredAt ? moment(summary.lastScoredAt).fromNow() : '—' }
  ];

  // Distribution histogram (buckets 1-10, always render the full range)
  const bucketCounts = _.keyBy(summary?.distribution || [], 'bucket');
  const maxBucketCount = _.max(_.map(summary?.distribution || [], 'count')) || 1;
  const histogramBuckets = _.range(1, 11).map(bucket => ({
    bucket,
    count: bucketCounts[bucket]?.count || 0,
    isPassing: bucket >= PASSING_SCORE
  }));

  const numPages = Math.ceil(total / pageSize);

  return (
    <div className='collection-analysis-outer'>
      <div className='collection-analysis-header'>
        <div>
          <div className='collection-analysis-title'>Collection Ranking</div>
          <div className='collection-analysis-subtitle'>
            LLM quality grades for Icon + Trendsetter collections. Collections scoring {PASSING_SCORE}/10 or higher are embedded into
            the collection search index.
          </div>
        </div>
        <div className={cn('collection-analysis-summary-stats', { loading: !summary })}>
          {summaryStats.map(({ label, value }) => (
            <div key={label} className='summary-stat'>
              <div className='value'>{value}</div>
              <div className='label'>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {!!summary?.totalScored && (
        <div className='collection-analysis-section'>
          <div className='collection-analysis-section-title'>Score distribution</div>
          <div className='collection-analysis-histogram'>
            {histogramBuckets.map(({ bucket, count, isPassing }) => (
              <div key={bucket} className='histogram-column'>
                <div className='histogram-count'>{count ? count.toLocaleString() : ''}</div>
                <div className='histogram-bar-well'>
                  <div className={cn('histogram-bar', { passing: isPassing })} style={{ height: `${(count / maxBucketCount) * 100}%` }} />
                </div>
                <div className='histogram-bucket'>{bucket}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className='collection-analysis-section'>
        <div className='collection-analysis-section-title'>Search test — queries the collection vector index</div>
        <CollectionSearch passingScore={PASSING_SCORE} />
      </div>

      <div className='collection-analysis-section'>
        <div className='collection-analysis-controls'>
          <div className='controls-search'>
            <SearchInput value={searchValue} onChange={onSearchChange} placeholder='Search by collection or creator' showSearchIcon />
          </div>
          <select value={minScore} onChange={e => changeMinScore(e.target.value)}>
            {MIN_SCORE_OPTIONS.map(({ label, value }) => (
              <option key={label} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select value={sortBy} onChange={e => changeSortBy(e.target.value)}>
            {SORT_OPTIONS.map(({ label, value }) => (
              <option key={value} value={value}>
                Sort: {label}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className='collection-analysis-empty'>Loading scored collections...</div>
        ) : !results.length ? (
          <div className='collection-analysis-empty'>
            {total === 0 && !query && !minScore
              ? 'No collections have been scored yet — run the calibration sample script to populate this table.'
              : 'No scored collections match these filters.'}
          </div>
        ) : (
          <div className='collection-analysis-results'>
            {results.map(result => (
              <CollectionAnalysisResult key={result.id} result={result} passingScore={PASSING_SCORE} saveAdjustment={saveAdjustment} />
            ))}
          </div>
        )}

        {numPages > 1 && (
          <div className='collection-analysis-pagination'>
            <button type='button' disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <span>
              Page {page + 1} of {numPages} · {total.toLocaleString()} collections
            </span>
            <button type='button' disabled={page >= numPages - 1} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionAnalysis;
